import { useQuery } from "@tanstack/react-query"
import { useTokensUsdPrices } from "../../hooks/useTokensUsdPrices"
import type { IntentsUserId } from "../../types/intentsUserId"
import { formatTokenValue } from "../../utils/format"
import { type ExchangeRate, formatBps, lstToNear } from "./amounts"
import { useNearStakingBalances } from "./balances"
import {
  getLstContractId,
  NEAR_DECIMALS,
  NEAR_STAKING_APR_BPS,
  WNEAR_ASSET_ID,
} from "./config"
import type {
  CurrentEpochEstimate,
  PoolSnapshot,
  UserWithdrawalRequest,
} from "./contract"
import {
  createCurrentEpochQueryOptions,
  createPoolSnapshotQueryOptions,
  createUserWithdrawalRequestQueryOptions,
} from "./queries"

export interface NearStakingPosition {
  aprLabel: string
  availableNear: bigint
  availableNearLabel: string
  availableUsd: number
  contractId: string | null
  currentEpoch: CurrentEpochEstimate | null | undefined
  enabled: boolean
  exchangeRate: ExchangeRate | null
  lstPriceLabel: string
  stakedLst: bigint
  stakedNear: bigint
  stakedNearLabel: string
  stakedUsd: number | null
  stakedUsdLoadError: boolean
  stakedUsdLoading: boolean
  totalUsd: number | null
  userId: IntentsUserId | null
  withdrawalRequest: NearStakingWithdrawalRequest | null | undefined
}

export interface NearStakingWithdrawalTranche {
  amount: bigint
  unstakeEpoch: number
  usdValue: number
}

export interface NearStakingWithdrawalRequest
  extends Omit<UserWithdrawalRequest, "tranches"> {
  tranches: NearStakingWithdrawalTranche[]
}

export interface ActiveNearStakingPosition extends NearStakingPosition {
  contractId: string
  enabled: true
  userId: IntentsUserId
}

export function isNearStakingActive(
  position: NearStakingPosition
): position is ActiveNearStakingPosition {
  return (
    position.enabled && position.contractId != null && position.userId != null
  )
}

const ONE_NEAR = 10n ** BigInt(NEAR_DECIMALS)

export function useNearStakingPosition(
  userId: IntentsUserId | null
): NearStakingPosition {
  const contractId = getLstContractId()
  const enabled = contractId != null && userId != null
  const { availableNear, stakedLst } = useNearStakingBalances(userId)
  const { data: poolSnapshot, isLoading: poolSnapshotLoading } = useQuery(
    createPoolSnapshotQueryOptions(contractId)
  )
  const { data: withdrawalRequest } = useQuery(
    createUserWithdrawalRequestQueryOptions(contractId, userId)
  )
  const hasWithdrawalRequest = (withdrawalRequest?.totalAmount ?? 0n) > 0n
  const { data: currentEpoch } = useQuery(
    createCurrentEpochQueryOptions(enabled && hasWithdrawalRequest)
  )
  const { data: prices, isPending: nearPriceLoading } = useTokensUsdPrices()
  const nearPrice = prices?.[WNEAR_ASSET_ID]?.price ?? null

  return computeNearStakingPosition({
    availableNear,
    contractId,
    currentEpoch,
    enabled,
    nearPrice,
    nearPriceLoading,
    poolSnapshot,
    poolSnapshotLoading,
    stakedLst,
    userId,
    withdrawalRequest,
  })
}

export function computeNearStakingPosition({
  availableNear,
  contractId,
  currentEpoch,
  enabled,
  nearPrice,
  nearPriceLoading = false,
  poolSnapshot,
  poolSnapshotLoading = false,
  stakedLst,
  userId,
  withdrawalRequest,
}: {
  availableNear: bigint
  contractId: string | null
  currentEpoch: CurrentEpochEstimate | null | undefined
  enabled: boolean
  nearPrice: number | null
  nearPriceLoading?: boolean
  poolSnapshot: PoolSnapshot | null | undefined
  poolSnapshotLoading?: boolean
  stakedLst: bigint
  userId: IntentsUserId | null
  withdrawalRequest: UserWithdrawalRequest | null | undefined
}): NearStakingPosition {
  const canEstimateStakedNear = poolSnapshot != null || stakedLst === 0n
  const canCalculateStakedUsd =
    canEstimateStakedNear && (nearPrice != null || stakedLst === 0n)
  const stakedUsdLoading =
    !canCalculateStakedUsd && (poolSnapshotLoading || nearPriceLoading)
  const stakedUsdLoadError =
    enabled && !canCalculateStakedUsd && !stakedUsdLoading
  const stakedNear =
    poolSnapshot == null ? 0n : lstToNear(stakedLst, poolSnapshot.rate)
  const lstNearPrice =
    poolSnapshot == null ? null : lstToNear(ONE_NEAR, poolSnapshot.rate)
  const pendingWithdrawal = withdrawalRequest?.totalAmount ?? 0n
  const totalNear = availableNear + stakedNear + pendingWithdrawal
  const nearPriceOrZero = nearPrice ?? 0
  const withdrawalRequestWithUsd = withdrawalRequest && {
    ...withdrawalRequest,
    tranches: withdrawalRequest.tranches.map((tranche) => ({
      ...tranche,
      usdValue: nearAmountToUsd(tranche.amount, nearPriceOrZero),
    })),
  }

  return {
    aprLabel: formatBps(NEAR_STAKING_APR_BPS),
    availableNear,
    availableNearLabel: formatNearForDisplay(availableNear),
    availableUsd: nearAmountToUsd(availableNear, nearPriceOrZero),
    contractId,
    currentEpoch,
    enabled,
    exchangeRate: poolSnapshot?.rate ?? null,
    lstPriceLabel:
      lstNearPrice == null
        ? "—"
        : formatTokenValue(lstNearPrice, NEAR_DECIMALS, {
            fractionDigits: 6,
          }),
    stakedLst,
    stakedNear,
    stakedNearLabel: canEstimateStakedNear
      ? formatNearForDisplay(stakedNear)
      : "—",
    stakedUsd: canCalculateStakedUsd
      ? nearAmountToUsd(stakedNear, nearPriceOrZero)
      : null,
    stakedUsdLoadError,
    stakedUsdLoading,
    totalUsd: canCalculateStakedUsd
      ? nearAmountToUsd(totalNear, nearPriceOrZero)
      : null,
    userId,
    withdrawalRequest: withdrawalRequestWithUsd,
  }
}

function formatNearForDisplay(amount: bigint): string {
  return formatTokenValue(amount, NEAR_DECIMALS, {
    fractionDigits: amount < ONE_NEAR ? 4 : 2,
  })
}

function nearAmountToUsd(amount: bigint, nearPrice: number): number {
  return Number(formatTokenValue(amount, NEAR_DECIMALS)) * nearPrice
}
